import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Button, Upload, message, Space, Switch } from 'antd';
import { UploadOutlined, EyeOutlined, EditOutlined, CopyOutlined, DownloadOutlined, CompressOutlined, ExpandOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { parseJsonForDisplay as safeParseJsonWithBigInt } from '@/lib/jsonValues';
import { formatJsonPreservingValues } from '@/lib/jsonValues';
import './index.scss';

// 直接导入CodeMirror模块
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { oneDark } from '@codemirror/theme-one-dark';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';

// 可折叠的JSON组件 - 从JsonViewer复制过来
const CollapsibleJson = React.forwardRef(({ data, theme = 'light', showToolbar = false }, ref) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(new Set());
  const keyboardFocus = useRef(null);

  const toggleCollapse = useCallback((path) => {
    setCollapsed(prev => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(path)) {
        newCollapsed.delete(path);
      } else {
        newCollapsed.add(path);
      }
      return newCollapsed;
    });
  }, []);

  const branchControl = useCallback((hasChildren, path, isCollapsed) => hasChildren ? {
    role: 'button',
    tabIndex: 0,
    'aria-expanded': !isCollapsed,
    'aria-label': t(isCollapsed ? 'jsonEditor.expandBranch' : 'jsonEditor.collapseBranch', { path: path || '$' }),
    ref: node => {
      // Expanded and collapsed branches have different DOM wrappers.
      if (node && keyboardFocus.current === path) {
        node.focus({ preventScroll: true });
        keyboardFocus.current = null;
      }
    },
    onClick: () => toggleCollapse(path),
    onKeyDown: event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        keyboardFocus.current = path;
        toggleCollapse(path);
      }
    }
  } : {}, [t, toggleCollapse]);

  const expandAll = useCallback(() => {
    setCollapsed(new Set());
  }, []);

  const collapseAll = useCallback(() => {
    const getAllPaths = (obj, currentPath = '') => {
      const paths = new Set();
      if (Array.isArray(obj)) {
        paths.add(currentPath);
        obj.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            const subPaths = getAllPaths(item, `${currentPath}[${index}]`);
            subPaths.forEach(p => paths.add(p));
          }
        });
      } else if (typeof obj === 'object' && obj !== null) {
        paths.add(currentPath);
        Object.keys(obj).forEach(key => {
          if (typeof obj[key] === 'object' && obj[key] !== null) {
            const subPaths = getAllPaths(obj[key], currentPath ? `${currentPath}.${key}` : key);
            subPaths.forEach(p => paths.add(p));
          }
        });
      }
      return paths;
    };
    setCollapsed(getAllPaths(data));
  }, [data]);

  // 通过ref暴露方法给父组件
  React.useImperativeHandle(ref, () => ({
    expandAll,
    collapseAll
  }), [expandAll, collapseAll]);

  const renderValue = useCallback((value, path = '', key = '', isLastItem = false) => {
    const currentPath = path ? (key ? `${path}.${key}` : path) : key;
    
    if (value === null) {
      return (
        <span className="json-line" key={currentPath}>
          <span className="json-null">null</span>
          {!isLastItem && <span className="json-comma">,</span>}
        </span>
      );
    }
    
    if (value === undefined) {
      return (
        <span className="json-line" key={currentPath}>
          <span className="json-undefined">undefined</span>
          {!isLastItem && <span className="json-comma">,</span>}
        </span>
      );
    }
    
    const valueType = typeof value;
    
    if (valueType === 'string') {
      return (
        <span className="json-line" key={currentPath}>
          <span className="json-string">{JSON.stringify(value)}</span>
          {!isLastItem && <span className="json-comma">,</span>}
        </span>
      );
    }
    
    if (valueType === 'number' || valueType === 'bigint') {
      return (
        <span className="json-line" key={currentPath}>
          <span className="json-number">{String(value)}</span>
          {!isLastItem && <span className="json-comma">,</span>}
        </span>
      );
    }
    
    if (valueType === 'boolean') {
      return (
        <span className="json-line" key={currentPath}>
          <span className="json-boolean">{value.toString()}</span>
          {!isLastItem && <span className="json-comma">,</span>}
        </span>
      );
    }
    
    if (Array.isArray(value)) {
      const isCollapsed = collapsed.has(currentPath);
      const hasItems = value.length > 0;
      
      if (isCollapsed) {
        return (
          <span className="json-line" key={currentPath}>
            <span 
              className={`json-bracket ${hasItems ? 'clickable' : ''}`}
              {...branchControl(hasItems, currentPath, isCollapsed)}
            >
              [
              {hasItems && (
                <span className="json-preview">...{value.length} items</span>
              )}
              ]
            </span>
            {!isLastItem && <span className="json-comma">,</span>}
          </span>
        );
      }
      
      return (
        <div className="json-array" key={currentPath}>
          <span className="json-line">
            <span 
              className={`json-bracket ${hasItems ? 'clickable' : ''}`}
              {...branchControl(hasItems, currentPath, isCollapsed)}
            >
              [
            </span>
          </span>
          {hasItems && (
            <div className="json-content">
              {value.map((item, index) => (
                <div key={`${currentPath}[${index}]`} className="json-item">
                  <span className="json-index">{index}: </span>
                  {renderValue(item, currentPath, `[${index}]`, index === value.length - 1)}
                </div>
              ))}
            </div>
          )}
          <span className="json-line">
            <span className="json-bracket">]</span>
            {!isLastItem && <span className="json-comma">,</span>}
          </span>
        </div>
      );
    }
    
    if (valueType === 'object') {
      const isCollapsed = collapsed.has(currentPath);
      const keys = Object.keys(value);
      const hasKeys = keys.length > 0;
      
      if (isCollapsed) {
        return (
          <span className="json-line" key={currentPath}>
            <span 
              className={`json-bracket ${hasKeys ? 'clickable' : ''}`}
              {...branchControl(hasKeys, currentPath, isCollapsed)}
            >
              {'{'}
              {hasKeys && (
                <span className="json-preview">...{keys.length} keys</span>
              )}
              {'}'}
            </span>
            {!isLastItem && <span className="json-comma">,</span>}
          </span>
        );
      }
      
      return (
        <div className="json-object" key={currentPath}>
          <span className="json-line">
            <span 
              className={`json-bracket ${hasKeys ? 'clickable' : ''}`}
              {...branchControl(hasKeys, currentPath, isCollapsed)}
            >
              {'{'}
            </span>
          </span>
          {hasKeys && (
            <div className="json-content">
              {keys.map((objKey, index) => (
                <div key={`${currentPath}.${objKey}`} className="json-item">
                  <span className="json-key">{JSON.stringify(objKey)}</span>
                  <span className="json-colon">: </span>
                  {renderValue(value[objKey], currentPath, objKey, index === keys.length - 1)}
                </div>
              ))}
            </div>
          )}
          <span className="json-line">
            <span className="json-bracket">{'}'}</span>
            {!isLastItem && <span className="json-comma">,</span>}
          </span>
        </div>
      );
    }
    
    return (
      <span className="json-line" key={currentPath}>
        <span className="json-value">{String(value)}</span>
        {!isLastItem && <span className="json-comma">,</span>}
      </span>
    );
  }, [collapsed, branchControl]);

  if (!data) {
    return <div className="json-empty">{t('jsonEditor.empty')}</div>;
  }

  return (
    <div className={`collapsible-json ${theme}`}>
      {showToolbar && (
        <div className="json-toolbar">
          <Space>
            <Button size="small" icon={<ExpandOutlined />} onClick={expandAll}>
              {t('jsonEditor.expand')}
            </Button>
            <Button size="small" icon={<CompressOutlined />} onClick={collapseAll}>
              {t('jsonEditor.collapse')}
            </Button>
          </Space>
        </div>
      )}
      <div className="json-root">
        {renderValue(data)}
      </div>
    </div>
  );
});

CollapsibleJson.displayName = 'CollapsibleJson';

const JsonEditor = React.forwardRef(function JsonEditor({
  value, 
  onChange, 
  placeholder = "请输入JSON配置", 
  label = "JSON配置",
  height = 300,
  maxHeight = 600,
  disabled = false,
  initialViewMode = false,
  onModeChange,
  renderView,
  id,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  theme = 'light' // 添加主题支持
}, ref) {
  const { t } = useTranslation();
  const [isViewMode, setIsViewMode] = useState(initialViewMode);
  const [useCodeMirror, setUseCodeMirror] = useState(true); // 默认启用CodeMirror
  const [feedback, setFeedback] = useState('');
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const jsonViewerRef = useRef(null);
  const codeMirrorRef = useRef(null);
  const importVersion = useRef(0);
  const current = useRef({ value, disabled });
  current.current = { value, disabled };
  useEffect(() => () => { importVersion.current++; }, []);
  useEffect(() => { if (disabled) importVersion.current++; }, [disabled]);
  const [focusRequested, setFocusRequested] = useState(false);
  React.useImperativeHandle(ref, () => ({ nativeElement: containerRef.current, focus: () => {
    if (disabled) return;
    setIsViewMode(false);
    setFocusRequested(true);
  } }), [disabled]);
  useEffect(() => {
    if (!focusRequested || isViewMode || disabled) return;
    const editor = useCodeMirror ? codeMirrorRef.current : textareaRef.current;
    if (editor) { editor.focus(); setFocusRequested(false); }
  }, [focusRequested, isViewMode, disabled, useCodeMirror]);
  const labelEditor = useCallback(view => {
    view.contentDOM.setAttribute('aria-label', label);
    if (describedBy) view.contentDOM.setAttribute('aria-describedby', describedBy);
    else view.contentDOM.removeAttribute('aria-describedby');
    view.contentDOM.setAttribute('aria-invalid', invalid ? 'true' : 'false');
  }, [label, describedBy, invalid]);
  useEffect(() => { if (codeMirrorRef.current) labelEditor(codeMirrorRef.current); }, [labelEditor]);

  // 检查CodeMirror是否可用
  useEffect(() => {
    const isCodeMirrorAvailable = !!(CodeMirror && json);
    setUseCodeMirror(isCodeMirrorAvailable);
  }, []);

  const parsed = useMemo(() => {
    if (!value?.trim()) return { data: null, error: false };
    try { return { data: safeParseJsonWithBigInt(value), error: false }; }
    catch { return { data: null, error: true }; }
  }, [value]);
  const parsedData = parsed.data;
  const jsonError = parsed.error ? t('jsonEditor.invalid') : '';

  // CodeMirror配置
  const codeMirrorExtensions = useMemo(() => {
    const extensions = [];
    if (json) {
      extensions.push(json());
    }
    // 添加默认语法高亮
    extensions.push(syntaxHighlighting(defaultHighlightStyle));
    return extensions;
  }, []);

  const codeMirrorTheme = useMemo(() => {
    return theme === 'dark' && oneDark ? oneDark : undefined;
  }, [theme]);

  // 使用useCallback缓存函数
  const handleFileUpload = useCallback((file) => {
    const sequence = ++importVersion.current;
    const original = current.current.value;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (sequence !== importVersion.current || current.current.disabled || current.current.value !== original) return;
      try {
        const content = e.target.result;
        // 验证是否为有效的JSON
        safeParseJsonWithBigInt(content);
        onChange?.(content);
        setFeedback(t('jsonEditor.importDone'));
        message.success(t('jsonEditor.importDone'));
      } catch (error) {
        setFeedback(t('jsonEditor.invalid'));
        message.error(t('jsonEditor.invalid'));
      }
    };
    reader.onerror = () => { if (sequence === importVersion.current) { setFeedback(t('jsonEditor.importFailed')); message.error(t('jsonEditor.importFailed')); } };
    reader.readAsText(file);
    return false; // 阻止默认上传行为
  }, [onChange, t]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      setFeedback(t('jsonEditor.copied'));
      message.success(t('jsonEditor.copied'));
    } catch (error) {
      setFeedback(t('jsonEditor.copyFailed'));
      message.error(t('jsonEditor.copyFailed'));
    }
  }, [value, t]);

  const handleDownload = useCallback(() => {
    if (!value) {
      message.warning(t('jsonEditor.empty'));
      return;
    }
    
    try {
      const blob = new Blob([value], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}-${new Date().getTime()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setFeedback(t('jsonEditor.downloadDone'));
      message.success(t('jsonEditor.downloadDone'));
    } catch (error) {
      setFeedback(t('jsonEditor.downloadFailed'));
      message.error(t('jsonEditor.downloadFailed'));
    }
  }, [value, label, t]);

  const formatJson = useCallback(() => {
    if (!value) return;
    
    try {
      const formatted = formatJsonPreservingValues(value);
      onChange?.(formatted);
      setFeedback(t('jsonEditor.formatted'));
      message.success(t('jsonEditor.formatted'));
    } catch (error) {
      message.error(t('jsonEditor.invalid'));
    }
  }, [value, onChange, t]);

  const handleCodeMirrorChange = useCallback((val) => {
    onChange?.(val);
  }, [onChange]);

  const handleTextareaChange = useCallback((e) => {
    const newValue = e.target.value;
    onChange?.(newValue);
  }, [onChange]);

  // 展开/收缩功能
  const handleExpandAll = useCallback(() => {
    if (jsonViewerRef.current) {
      jsonViewerRef.current.expandAll();
    }
  }, []);

  const handleCollapseAll = useCallback(() => {
    if (jsonViewerRef.current) {
      jsonViewerRef.current.collapseAll();
    }
  }, []);

  // 渲染编辑器
  const renderEditor = () => {
    if (useCodeMirror && CodeMirror) {
      return (
        <div className="json-editor-codemirror" style={{ height: '100%' }}>
            <CodeMirror
              id={id}
              onCreateEditor={view => {
                codeMirrorRef.current = view; labelEditor(view);
                if (focusRequested && !disabled) { view.focus(); setFocusRequested(false); }
              }}
              value={value || ''}
              onChange={handleCodeMirrorChange}
              extensions={codeMirrorExtensions}
              theme={codeMirrorTheme}
              placeholder={placeholder}
              editable={!disabled}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                indentOnInput: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true
              }}
              style={{
                height: '100%',
                fontSize: '13px',
                fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace"
              }}
            />
        </div>
      );
    }

    // Fallback到textarea
    return (
      <textarea
        id={id}
        aria-label={label}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        ref={textareaRef}
        className={`json-editor-textarea ${jsonError ? 'error' : ''}`}
        value={value || ''}
        onChange={handleTextareaChange}
        placeholder={placeholder}
        disabled={disabled}
        style={{ 
          height: '100%',
          width: '100%',
          resize: 'none'
        }}
      />
    );
  };

  return (
    <div className="json-editor" ref={containerRef}>
      {/* 工具栏 */}
      <div className="json-editor-toolbar">
        <div className="toolbar-left">
          <Space size={6}>
            <Switch
              aria-label={t('jsonEditor.viewMode')}
              size="small"
              checked={isViewMode}
              onChange={next => { setIsViewMode(next); onModeChange?.(next); }}
              checkedChildren={<EyeOutlined />}
              unCheckedChildren={<EditOutlined />}
              disabled={disabled}
            />
            <span className="mode-label">
              {isViewMode ? t('jsonEditor.viewMode') : t('jsonEditor.editMode')}
              {!isViewMode && !useCodeMirror && <span style={{ color: '#999', marginLeft: 4 }}>{t('jsonEditor.plainText')}</span>}
            </span>
          </Space>
        </div>
        
        <div className="toolbar-right">
          <Space size={6}>
            {/* 查看模式下显示展开/收缩按钮 */}
            {isViewMode && parsedData && !renderView && (
              <>
                <Button size="small" icon={<ExpandOutlined />} onClick={handleExpandAll}>
                  {t('jsonEditor.expand')}
                </Button>
                <Button size="small" icon={<CompressOutlined />} onClick={handleCollapseAll}>
                  {t('jsonEditor.collapse')}
                </Button>
                <span style={{ width: '1px', height: '14px', backgroundColor: '#d9d9d9', margin: '0 2px' }} />
              </>
            )}
            
            <Upload
              beforeUpload={handleFileUpload}
              showUploadList={false}
              accept=".json,.txt"
              disabled={disabled}
            >
              <Button size="small" icon={<UploadOutlined />} disabled={disabled}>
                {t('jsonEditor.importFile')}
              </Button>
            </Upload>
            
            {!isViewMode && (
              <Button size="small" onClick={formatJson} disabled={disabled || !value}>
                {t('jsonEditor.format')}
              </Button>
            )}
            
            <Button size="small" icon={<CopyOutlined />} onClick={handleCopy} disabled={!value}>
              {t('jsonEditor.copy')}
            </Button>
            
            <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload} disabled={!value}>
              {t('jsonEditor.download')}
            </Button>
          </Space>
        </div>
      </div>

      {/* 错误提示 */}
      {jsonError && (
        <div className="json-editor-error">
          <span role="alert">{jsonError}</span>
        </div>
      )}

      {/* 内容区域 */}
      <div 
        className="json-editor-content" 
        style={{ 
          height: height, 
          maxHeight: maxHeight,
          overflow: 'hidden'
        }}
      >
        {isViewMode ? (
          // 查看模式 - 使用CollapsibleJson组件
          <div 
            className="json-viewer-container"
            style={{
              height: '100%',
              overflow: 'auto'
            }}
          >
            {renderView ? renderView(value || '') : jsonError ? <pre tabIndex={0} aria-label={label}>{value}</pre> : !value ? <p>{t('jsonEditor.empty')}</p> : <CollapsibleJson
              ref={jsonViewerRef}
              data={parsedData} 
              theme="light" 
              showToolbar={false}
            />}
          </div>
        ) : (
          // 编辑模式 - 使用CodeMirror或textarea
          renderEditor()
        )}
      </div>

      {/* 状态栏 */}
      <div className="json-editor-status">
        <span className="json-editor-feedback" role="status" aria-live="polite">{feedback}</span>
        {value ? (
          <span className="status-info">
            {t('jsonEditor.characters', { count: value.length })} | {jsonError || t('jsonEditor.valid')}
          </span>
        ) : (
          <span className="status-empty">{t('jsonEditor.empty')}</span>
        )}
      </div>
    </div>
  );
});

export default JsonEditor;
